var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable import/no-extraneous-dependencies, @typescript-eslint/naming-convention */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { HttpResponseError, SSE_BACKOFF_JITTER_MAX_MS, SSE_INITIAL_BACKOFF_MS, SSE_MAX_BACKOFF_MS } from '../../../hooks/useQuerySubscription';
import { requestAiToken } from '../services/aiTokenApi';
import { getStoredAiToken, setStoredAiToken } from '../services/aiTokenStorage';
import { getOrchestratorBaseUrl } from '../services/orchestrator-api-client';
import { SUPPORT_SSE_EVENT, SUPPORT_STREAM_IDLE_TIMEOUT_MS, SUPPORT_STREAM_WATCHDOG_TICK_MS } from '../constants/support';
import { trackStreamActivity } from '../helpers/trackStreamActivity';
// ---------------------------------------------------------------------------
// GET /api/chat/{conversation_id}/support/events?after_sequence=
//
// A dedicated, long-lived stream that is independent of the per-AI-response stream
// (it never sends `stream_end`). It is NOT built on useQuerySubscription because the
// reconnect cursor must be re-read on EVERY attempt: fetchEventSource's own retry reuses
// the original URL, so a reconnect after the tab slept would replay from a stale
// `after_sequence`. Here every attempt asks for exactly what we have not yet seen.
// ---------------------------------------------------------------------------
export function getSupportEventsUrl(conversationId, afterSequence) {
    return `${getOrchestratorBaseUrl()}/chat/${conversationId}/support/events?after_sequence=${afterSequence}`;
}
const SSE_EVENT_NAMES = new Set(Object.values(SUPPORT_SSE_EVENT));
const HTTP_UNAUTHORIZED = 401;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_CLIENT_ERROR_MIN = 400;
const HTTP_SERVER_ERROR_MIN = 500;
/** 4xx other than auth-expiry / timeout / rate-limit will not get better by retrying. */
function isPermanentFailure(error) {
    if (!(error instanceof HttpResponseError))
        return false;
    const { status } = error;
    if (status === HTTP_REQUEST_TIMEOUT || status === HTTP_TOO_MANY_REQUESTS)
        return false;
    return status >= HTTP_CLIENT_ERROR_MIN && status < HTTP_SERVER_ERROR_MIN;
}
function backoffMs(attempt) {
    const base = Math.min(SSE_INITIAL_BACKOFF_MS * 2 ** Math.max(0, attempt - 1), SSE_MAX_BACKOFF_MS);
    return base + Math.random() * SSE_BACKOFF_JITTER_MAX_MS;
}
export function useSupportEventStream({ conversationId, enabled, getAfterSequence, onEvent }) {
    const [connectionState, setConnectionState] = useState('idle');
    const [reconnectNonce, setReconnectNonce] = useState(0);
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;
    const getAfterSequenceRef = useRef(getAfterSequence);
    getAfterSequenceRef.current = getAfterSequence;
    const reconnect = useCallback(() => setReconnectNonce((n) => n + 1), []);
    useEffect(() => {
        if (!enabled || !conversationId) {
            setConnectionState('idle');
            return undefined;
        }
        let disposed = false;
        let controller = null;
        let retryTimer = null;
        let attempts = 0;
        let authRefreshed = false;
        // The last time ANY byte arrived (data, or the heartbeat comment no handler ever sees).
        let lastActivityAt = Date.now();
        const touch = () => {
            lastActivityAt = Date.now();
        };
        // A failure that retrying cannot fix (4xx, a second 401): the watchdog must not keep poking it.
        let halted = false;
        const clearRetry = () => {
            if (retryTimer !== null) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
        };
        const scheduleRetry = () => {
            if (disposed)
                return;
            attempts += 1;
            setConnectionState('reconnecting');
            clearRetry();
            retryTimer = setTimeout(connect, backoffMs(attempts)); // eslint-disable-line @typescript-eslint/no-use-before-define
        };
        const handleFailure = (error) => __awaiter(this, void 0, void 0, function* () {
            if (disposed)
                return;
            if (error instanceof Error && error.name === 'AbortError')
                return;
            if (error instanceof HttpResponseError && error.status === HTTP_UNAUTHORIZED) {
                if (authRefreshed) {
                    // One refresh per failure streak; a second 401 means the Rails session itself is gone.
                    halted = true;
                    setConnectionState('error');
                    return;
                }
                authRefreshed = true;
                try {
                    const response = yield requestAiToken();
                    if (response.data)
                        setStoredAiToken(response.data.token, response.data.expires_at);
                }
                catch (_a) {
                    // Retry with whatever token we hold; a permanent 401 surfaces on the next pass.
                }
                scheduleRetry();
                return;
            }
            if (isPermanentFailure(error)) {
                halted = true;
                setConnectionState('error');
                return;
            }
            scheduleRetry();
        });
        function connect() {
            if (disposed)
                return;
            clearRetry();
            controller === null || controller === void 0 ? void 0 : controller.abort();
            const attemptController = new AbortController();
            controller = attemptController;
            touch(); // a fresh attempt gets the full idle window, including time spent opening
            halted = false;
            setConnectionState(attempts === 0 ? 'connecting' : 'reconnecting');
            const token = getStoredAiToken();
            void fetchEventSource(getSupportEventsUrl(conversationId, getAfterSequenceRef.current()), {
                headers: Object.assign({ Accept: EventStreamContentType }, (token ? { Authorization: `Bearer ${token}` } : {})),
                signal: attemptController.signal,
                openWhenHidden: true,
                // Reports raw bytes (heartbeat comments included) to the idle watchdog below.
                fetch: (input, init) => __awaiter(this, void 0, void 0, function* () { return trackStreamActivity(yield window.fetch(input, init), touch); }),
                // eslint-disable-next-line @typescript-eslint/require-await
                onopen: (response) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    touch();
                    if (response.ok && ((_a = response.headers.get('content-type')) === null || _a === void 0 ? void 0 : _a.includes(EventStreamContentType))) {
                        attempts = 0;
                        authRefreshed = false;
                        setConnectionState('connected');
                        return;
                    }
                    throw new HttpResponseError(response.status, response.statusText);
                }),
                onmessage: (event) => {
                    touch();
                    if (!SSE_EVENT_NAMES.has(event.event) || !event.data)
                        return;
                    try {
                        const envelope = JSON.parse(event.data);
                        onEventRef.current(event.event, envelope);
                    }
                    catch (parseError) {
                        console.error('[useSupportEventStream] Failed to process event:', event.event, parseError);
                    }
                },
                // Throwing stops fetchEventSource's own retry loop; ours re-reads the cursor.
                onerror: (error) => {
                    throw error;
                }
            }).then(() => {
                // The server closed the stream. It is meant to stay open, so treat it as a drop.
                if (attemptController === controller)
                    scheduleRetry();
            }, (error) => {
                if (attemptController === controller)
                    void handleFailure(error);
            });
        }
        // Backgrounding (iOS webview, a sleeping laptop tab) silently kills the socket without
        // an error event. On return to the foreground drop whatever we hold and catch up.
        const reconnectNow = () => {
            attempts = 0;
            connect();
        };
        const handleVisibility = () => {
            if (document.visibilityState === 'visible')
                reconnectNow();
        };
        // Idle watchdog. Nothing tells a client that a silently dropped socket is dead, so: no bytes
        // for the idle window while the tab is visible means reconnect (re-reading the cursor, so the
        // catch-up asks for exactly what was missed). Skipped while a backoff retry is already
        // pending, after a failure retrying cannot fix, and while hidden (the visibility handler
        // reconnects on return).
        const checkIdle = () => {
            if (disposed || halted || retryTimer !== null || document.visibilityState === 'hidden')
                return;
            if (Date.now() - lastActivityAt >= SUPPORT_STREAM_IDLE_TIMEOUT_MS)
                reconnectNow();
        };
        connect();
        const watchdog = setInterval(checkIdle, SUPPORT_STREAM_WATCHDOG_TICK_MS);
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('online', reconnectNow);
        return () => {
            disposed = true;
            clearInterval(watchdog);
            clearRetry();
            controller === null || controller === void 0 ? void 0 : controller.abort();
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('online', reconnectNow);
        };
    }, [conversationId, enabled, reconnectNonce]);
    return { connectionState, reconnect };
}
